import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { LuRefreshCw, LuCheck } from 'react-icons/lu';
import { Alert, Button, LoadingSpinner } from 'components/common';
import { fetchPaymentPlanRates, applyPaymentPlanRates } from './api';
import PaymentPlanRatesTable from './PaymentPlanRatesTable';
import ApplyRatesModal from './ApplyRatesModal';

// Admin version of `rake payment_plans:stamp_rates`: payment plan milestones
// recorded in VND/THB get a USD exchange rate (contract rate, else Vietcombank
// today) so "% of Contract" stops adding mixed currencies. The list is a dry run
// computed by the server; nothing is written until rows are ticked and confirmed.
const PaymentPlanRates = () => {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchPaymentPlanRates());
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
      setSelected(new Set());
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applicable = useMemo(() => rows.filter(r => !r.error), [rows]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((select) => {
    setSelected(select ? new Set(applicable.map(r => r.schedule_id)) : new Set());
  }, [applicable]);

  const selectedRows = useMemo(() => rows.filter(r => selected.has(r.schedule_id)), [rows, selected]);

  const confirmApply = useCallback(async () => {
    setSubmitting(true);
    try {
      const result = await applyPaymentPlanRates(selectedRows.map(r => r.schedule_id));
      const skipped = result.skipped_ids?.length ? ` ${result.skipped_ids.length} skipped (no longer needed or rate unavailable).` : '';
      toast.success(`Updated ${result.applied_ids?.length || 0} payment plan(s).${skipped}`);
      setConfirming(false);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  }, [selectedRows, load]);

  return (
    <div className="tw-px-5 tw-py-5 tw-flex tw-flex-col tw-gap-4">
      <Alert
        type="info"
        title="Payment plan exchange rates"
        message="Milestones recorded in a currency other than USD (e.g. VND on a contract converted to USD) are converted to USD for % of Contract. Uses the contract's exchange rate; without one, today's Vietcombank rate — adjusted automatically once the contract has a rate. Released plans get RECEIVED UP TO recalculated; no email is sent and past release emails are unchanged."
      />

      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-flex-wrap">
        <div className="tw-text-[12.5px] tw-text-slate-500">
          {rows.length === 0 ? 'Nothing to convert' : `${rows.length} payment plan(s) need rates · ${selected.size} selected`}
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <Button variant="secondary" size="sm" leftIcon={LuRefreshCw} onClick={load} disabled={loading}>Refresh</Button>
          <Button variant="primary" size="sm" leftIcon={LuCheck} onClick={() => setConfirming(true)} disabled={selected.size === 0}>
            Apply rates
          </Button>
        </div>
      </div>

      {error && <Alert type="error" title="Could not load" message={error} />}

      {loading ? (
        <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner /></div>
      ) : (
        <PaymentPlanRatesTable
          rows={rows}
          selected={selected}
          onToggle={toggle}
          onToggleAll={toggleAll}
          allSelected={applicable.length > 0 && selected.size === applicable.length}
        />
      )}

      <ApplyRatesModal
        show={confirming}
        rows={selectedRows}
        submitting={submitting}
        onHide={() => setConfirming(false)}
        onConfirm={confirmApply}
      />
    </div>
  );
};

export default PaymentPlanRates;
